import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { saveNode } from "actions/workflows";
import { getNode } from "services/workflows";
import Input from "components/Elements/Input";
import ErrorField from "components/ErrorField";
import NiceModal from "@ebay/nice-modal-react";
import CodeMirror from "@uiw/react-codemirror";
import { json } from "@codemirror/lang-json";
import { python } from "@codemirror/lang-python";
import { EditorView } from "@codemirror/view";
import { createGlobalStyle } from "styled-components";

// components
import CardFullTable from "components/Cards/Admin/CardFullTable.js";
// texts as props
import Select from "components/Elements/Select";

const GlobalStyle = createGlobalStyle`
  .selectedView {
    font-weight: bold;
  }

  .view {
    cursor: pointer;
  }

  .view:hover {
    text-decoration: underline;
  }
`;

const NodeForm = ({
  node_id = null,
  archive_id = null,
  clickedClose = null,
  onSaved = () => {},
}) => {
  const { savingNode, functionToAdd } = useSelector((state) => state.workflows);
  const [node, setNode] = useState(null);
  const [localNode, setLocalNode] = useState({
    id: "",
    public_id: "",
    name: "",
    description: "",
    functions: [],
    properties: "",
    input_params: `{"input": {"type": "string"}}`,
    output_params: `{"output": {"type": "string"}}`,
  });
  const [inputParametersJSON, setInputParametersJSON] = useState("");
  const [inputParameters, setInputParameters] = useState([]);
  const [inputParametersCount, setInputParametersCount] = useState(0);
  const [inputParametersView, setInputParametersView] = useState("fields");
  const [outputParametersJSON, setOutputParametersJSON] = useState("");
  const [outputParameters, setOutputParameters] = useState([]);
  const [outputParametersCount, setOutputParametersCount] = useState(0);
  const [outputParametersView, setOutputParametersView] = useState("fields");

  const { activeUser } = useSelector((state) => state.users);
  const [formErrors, setFormErrors] = useState({});
  const [functionsArray, setFunctionsArray] = useState([]);
  const functionsHead = ["id", "public_id", "name", "description", "actions"];
  const dispatch = useDispatch();

  useEffect(() => {
    const loadNode = async (node_id, archive_id) => {
      const node = await getNode(node_id, archive_id);
      console.log("!!! NODE: ", node);
      setNode({ ...node });
      setLocalNode({ ...node });
    };

    loadNode(node_id, archive_id);

    // eslint-disable-next-line
  }, [node_id, archive_id]);

  useEffect(() => {
    if (functionToAdd) {
      const functionToAddArray = [
        functionToAdd.id,
        functionToAdd.public_id,
        functionToAdd.name,
        functionToAdd.description,
        <></>,
      ];
      setFunctionsArray([...functionsArray, functionToAddArray]);
      setLocalNode({
        ...localNode,
        functions: [...localNode.functions, functionToAdd],
      });
    }
    // eslint-disable-next-line
  }, [functionToAdd]);

  useEffect(() => {
    //dispatch(clearNode());
    if (!node && !node_id) {
      return;
    }

    if (node) {
      if (!node.functions) {
        node.functions = [];
      } else {
        const functions = node.functions.map((f) => {
          const values = [f.id, f.public_id, f.name, f.description, <></>];
          return values;
        });
        setFunctionsArray(functions);
      }
    }
    setLocalNode(node);
    // eslint-disable-next-line
  }, [node]);

  useEffect(() => {
    if (localNode?.input_params) {
      const input_params = JSON.parse(localNode.input_params);
      const keys = Object.keys(input_params);
      setInputParametersJSON(localNode.input_params);
      setInputParametersCount(keys.length);
      setInputParameters(keys.map((key) => [key, input_params[key].type]));
    }

    if (localNode?.output_params) {
      try {
        const output_params = JSON.parse(localNode.output_params);
        const keys = Object.keys(output_params);
        setOutputParametersJSON(localNode.output_params);
        setOutputParametersCount(keys.length);
        setOutputParameters(keys.map((key) => [key, output_params[key].type]));
      } catch (ex) {}
    }

    if (localNode?.id) {
      console.log("!!! LOCAL NODE UPDATED: ", localNode);
      onSaved({ ...localNode });
    }
    // eslint-disable-next-line
  }, [localNode]);

  useEffect(() => {
    const input_params = {};
    inputParameters.forEach((param) => {
      input_params[param[0]] = { type: param[1] };
    });
    const input_params_json = JSON.stringify(input_params, null, 2);
    setInputParametersJSON(input_params_json);
  }, [inputParameters]);

  useEffect(() => {
    const output_params = {};
    outputParameters.forEach((param) => {
      output_params[param[0]] = { type: param[1] };
    });
    const output_params_json = JSON.stringify(output_params, null, 2);
    setOutputParametersJSON(output_params_json);
  }, [outputParameters]);

  const editFunction = (row) => {
    const funct = {};
    functionsHead.forEach((key, index) => {
      funct[key] = row[index];
    });
    NiceModal.show("add-function", { funct_id: funct.public_id }).then(() => {
      if (node_id) {
        dispatch(getNode(node_id));
      }
    });
    //navigate(`/agent/${agent.public_id}`);
  };

  const handleCheckboxChange = ({ target }) => {
    const u = { ...localNode };
    u[target.name] = target.checked;
    setLocalNode(u);
  };

  const handleChange = ({ target }) => {
    const a = { ...localNode };
    a[target.name] = target.value;
    setLocalNode(a);
  };

  const handleCodeChange = (name, code) => {
    const a = { ...localNode };
    a[name] = code;
    setLocalNode(a);
  };

  /*
  const handleLLMChange = (llm) => {
    const a = { ...localNode };
    a.llm = llm;
    setLocalNode(a);
  };
  */

  const validateFields = () => {
    setFormErrors({});
    const errors = {};

    const nameValid = localNode.name && localNode.name.length > 0;

    errors.name = nameValid ? "" : " is required";

    setFormErrors(errors);

    return nameValid;
  };

  const save = () => {
    if (validateFields()) {
      const saveLocalNode = { ...localNode };
      saveLocalNode.input_params = inputParametersJSON;
      saveLocalNode.output_params = outputParametersJSON;
      setLocalNode(saveLocalNode);
      dispatch(saveNode(saveLocalNode));
    }
  };

  const renderInputParameters = () => {
    const inputs = [];
    for (let i = 0; i < inputParametersCount; i++) {
      inputs.push(
        <div key={`input_${i}`}>
          <div style={{ display: "flex", flexDirection: "row" }}>
            <Input
              label={inputParameters[i][0]}
              type="text"
              name={`input_params_${i}`}
              value={inputParameters[i][0]}
              onChange={(e) => {
                const newParams = [...inputParameters];
                newParams[i][0] = e.target.value;
                setInputParameters(newParams);
              }}
              style={{ width: "350px", marginRight: "10px" }}
            />
            <Select
              label="Type"
              placeholder="Type"
              name={`input_params_${i}_type`}
              defaultValue={inputParameters[i][1]}
              items={[
                "string",
                "number",
                "boolean",
                "dict",
                "string_array",
                "number_array",
              ]}
              onChange={(value) => {
                const newParams = [...inputParameters];
                newParams[i][1] = value;
                setInputParameters(newParams);
              }}
            />
            <span
              style={{ marginLeft: "5px", cursor: "pointer" }}
              onClick={() => {
                const newParams = [...inputParameters];
                newParams.splice(i, 1);
                setInputParameters(newParams);
                setInputParametersCount(newParams.length);
                setInputParametersJSON(JSON.stringify(newParams, null, 2));
              }}
            >
              <i className="fa fa-trash text-sm cursor-pointer" />
            </span>
          </div>
        </div>
      );
    }

    return (
      <div>
        <div style={{ display: "flex", flexDirection: "row" }}>
          <div style={{ width: "350px", marginRight: "10px" }}>Key</div>
          <div>Type</div>
        </div>
        {inputs}
      </div>
    );
  };

  const renderOutputParameters = () => {
    const outputs = [];
    for (let i = 0; i < outputParametersCount; i++) {
      outputs.push(
        <div key={`output_${i}`}>
          <div style={{ display: "flex", flexDirection: "row" }}>
            <Input
              label={outputParameters[i][0]}
              type="text"
              name={`output_params_${i}`}
              value={outputParameters[i][0]}
              onChange={(e) => {
                const newParams = [...outputParameters];
                newParams[i][0] = e.target.value;
                setOutputParameters(newParams);
              }}
              style={{ width: "350px", marginRight: "10px" }}
            />
            <Select
              label="Type"
              placeholder="Type"
              name={`output_params_${i}_type`}
              defaultValue={outputParameters[i][1]}
              items={[
                "string",
                "number",
                "boolean",
                "dict",
                "string_array",
                "number_array",
              ]}
              onChange={(value) => {
                const newParams = [...outputParameters];
                newParams[i][1] = value;
                setOutputParameters(newParams);
              }}
            />
            <span
              style={{ marginLeft: "5px", cursor: "pointer" }}
              onClick={() => {
                const newParams = [...outputParameters];
                newParams.splice(i, 1);
                setOutputParameters(newParams);
                setOutputParametersCount(newParams.length);
                setOutputParametersJSON(JSON.stringify(newParams, null, 2));
              }}
            >
              <i className="fa fa-trash text-sm cursor-pointer" />
            </span>
          </div>
        </div>
      );
    }

    return (
      <div>
        <div style={{ display: "flex", flexDirection: "row" }}>
          <div style={{ width: "350px", marginRight: "10px" }}>Key</div>
          <div>Type</div>
        </div>
        {outputs}
      </div>
    );
  };

  if (!localNode && node_id) return <>Loading...</>;

  return (
    <>
      <GlobalStyle />
      <div className="my-4 text-blueGray-500 text-lg leading-relaxed">
        <label>ID</label>
        <Input
          label="ID"
          placeholder="ID"
          name="id"
          value={localNode.id ?? ""}
          disabled
        />
        <label>Public ID</label>
        <Input
          label="Public ID"
          placeholder="Public ID"
          name="public_id"
          value={localNode?.public_id ?? ""}
          disabled
        />
        <label>
          Name <ErrorField>{formErrors.name}</ErrorField>
        </label>
        <Input
          label="Name"
          placeholder="Name"
          name="name"
          value={localNode?.name ?? ""}
          onChange={handleChange}
        />
        <label>
          Description <ErrorField>{formErrors.description}</ErrorField>
        </label>
        <Input
          type="textarea"
          label="Description"
          placeholder="Description"
          name="description"
          value={localNode?.description ?? ""}
          onChange={handleChange}
          style={{ height: "100px" }}
        />
        <label>
          Input Parameters (
          <span
            className={
              inputParametersView === "fields" ? "selectedView" : "view"
            }
            style={{ marginLeft: "5px", marginRight: "5px" }}
            onClick={() => setInputParametersView("fields")}
          >
            Fields
          </span>{" "}
          |{" "}
          <span
            className={inputParametersView === "json" ? "selectedView" : "view"}
            style={{ marginLeft: "5px", marginRight: "5px" }}
            onClick={() => setInputParametersView("json")}
          >
            JSON
          </span>
          )<ErrorField>{formErrors.input_params}</ErrorField>
          <span
            style={{ marginLeft: "5px", cursor: "pointer" }}
            onClick={() => {
              const newParams = [...inputParameters];
              newParams.push([`key_${newParams.length + 1}`, "string"]);
              setInputParameters(newParams);
              setInputParametersCount(newParams.length);
              setInputParametersJSON(JSON.stringify(newParams, null, 2));
            }}
          >
            <i className="fa fa-plus text-sm cursor-pointer" />
          </span>
        </label>
        <CodeMirror
          extensions={[json(), EditorView.lineWrapping]}
          name="input_params"
          value={inputParametersJSON ?? ""}
          onChange={(code) => handleCodeChange("input_params", code)}
          style={{
            width: "calc(100vw - 350px)",
            display: inputParametersView === "json" ? "block" : "none",
          }}
        />
        <div
          style={{
            display: inputParametersView === "fields" ? "block" : "none",
          }}
        >
          {renderInputParameters()}
        </div>
        <label>
          Output Parameters (
          <span
            className={
              outputParametersView === "fields" ? "selectedView" : "view"
            }
            style={{ marginLeft: "5px", marginRight: "5px" }}
            onClick={() => setOutputParametersView("fields")}
          >
            Fields
          </span>{" "}
          |{" "}
          <span
            className={
              outputParametersView === "json" ? "selectedView" : "view"
            }
            style={{ marginLeft: "5px", marginRight: "5px" }}
            onClick={() => setOutputParametersView("json")}
          >
            JSON
          </span>
          )<ErrorField>{formErrors.output_params}</ErrorField>
          <span
            style={{ marginLeft: "5px", cursor: "pointer" }}
            onClick={() => {
              const newParams = [...outputParameters];
              newParams.push([`key_${newParams.length + 1}`, "string"]);
              setOutputParameters(newParams);
              setOutputParametersCount(newParams.length);
              setOutputParametersJSON(JSON.stringify(newParams, null, 2));
            }}
          >
            <i className="fa fa-plus text-sm cursor-pointer" />
          </span>
        </label>
        <CodeMirror
          extensions={[json(), EditorView.lineWrapping]}
          label="Output Parameters"
          name="output_params"
          value={outputParametersJSON ?? ""}
          onChange={(code) => handleCodeChange("output_params", code)}
          style={{
            width: "calc(100vw - 350px)",
            display: outputParametersView === "json" ? "block" : "none",
          }}
        />

        <div
          style={{
            display: outputParametersView === "fields" ? "block" : "none",
          }}
        >
          {renderOutputParameters()}
        </div>

        <label>
          Properties (JSON)<ErrorField>{formErrors.properties}</ErrorField>
        </label>
        <CodeMirror
          extensions={[json(), EditorView.lineWrapping]}
          name="properties"
          value={localNode && localNode.properties ? localNode.properties : ""}
          onChange={(code) => handleCodeChange("properties", code)}
          style={{ width: "calc(100vw - 350px)" }}
        />
        <label>
          Code (Python)<ErrorField>{formErrors.code}</ErrorField>
        </label>
        <CodeMirror
          extensions={[python(), EditorView.lineWrapping]}
          label="Code"
          name="code"
          value={localNode && localNode.code ? localNode.code : ""}
          onChange={(code) => handleCodeChange("code", code)}
          style={{ width: "calc(100% - 5px)" }}
        />
        <div
          style={{
            display: "flex",
            flexDirection: "row",
            marginTop: "10px",
          }}
        >
          <label>
            Use LLM <ErrorField>{formErrors.use_llm}</ErrorField>
          </label>
          <Input
            type="checkbox"
            label="Use LLM"
            name="use_llm"
            checked={localNode.use_llm ?? false}
            onChange={handleCheckboxChange}
            style={{
              height: "20px",
              width: "20px",
              padding: "0px",
              marginLeft: "10px",
            }}
          />
        </div>
        {localNode && localNode.use_llm && (
          <>
            <label>
              LLM <ErrorField>{formErrors.llm}</ErrorField>
            </label>
            {/*
            <Select
              label="LLM"
              placeholder="LLM"
              name="llm"
              defaultValue={localNode.llm ?? "gpt-3.5-turbo"}
              items={languageModels}
              onChange={handleLLMChange}
            />
            */}
            <Input
              label="LLM"
              placeholder="Agentwire-GPT-4o"
              name="llm"
              value={localNode.llm}
              onChange={handleChange}
            />
            <label>
              Max Tokens <ErrorField>{formErrors.max_tokens}</ErrorField>
            </label>
            <Input
              label="Max Tokens"
              min="0"
              type="number"
              placeholder="Max Tokens"
              name="max_tokens"
              value={localNode.max_tokens ?? 1000}
              onChange={handleChange}
            />
            <label>
              Temperature <ErrorField>{formErrors.temperature}</ErrorField>
            </label>
            <Input
              type="number"
              min="0"
              max="1"
              step="0.01"
              label="Temperature"
              placeholder="Temperature"
              name="temperature"
              value={localNode.temperature ?? 0.0}
              onChange={handleChange}
            />
            <label>
              System Message{" "}
              <ErrorField>{formErrors.system_message}</ErrorField>
            </label>
            <Input
              type="textarea"
              placeholder="System Message"
              name="system_message"
              value={localNode.system_message ?? ""}
              onChange={handleChange}
            />
            <label>
              Prompt <ErrorField>{formErrors.prompt}</ErrorField>
            </label>
            <Input
              type="textarea"
              label="Prompt"
              placeholder="Prompt"
              name="prompt"
              value={localNode.prompt ?? ""}
              onChange={handleChange}
              style={{
                height: "150px",
                backgroundColor: "white",
                border: "1px solid #d1d5db",
                borderRadius: "0.375rem",
                fontSize: "0.875rem",
              }}
            />
            <label>
              Post LLM Code (Python)<ErrorField>{formErrors.code}</ErrorField>
            </label>
            <CodeMirror
              extensions={[python(), EditorView.lineWrapping]}
              label="Post LLM Code"
              name="post_llm_code"
              value={
                localNode && localNode.post_llm_code
                  ? localNode.post_llm_code
                  : ""
              }
              onChange={(code) => handleCodeChange("post_llm_code", code)}
              style={{ width: "calc(100% - 5px)" }}
            />
            <div className="w-full">
              <CardFullTable
                title="Functions"
                head={functionsHead}
                body={functionsArray ?? []}
                titlebutton={
                  <i
                    onClick={() => NiceModal.show("add-function-to-node")}
                    className="fa fa-plus text-sm cursor-pointer"
                  />
                }
                onRowClick={(row) => editFunction(row)}
              />
            </div>
          </>
        )}
      </div>
      <div className="flex items-center justify-end p-6 border-t border-solid border-blueGray-200 rounded-b">
        {!savingNode && clickedClose !== null ? (
          <button
            className="text-red-500 background-transparent font-bold uppercase px-6 py-2 text-sm outline-none focus:outline-none mr-1 mb-1 ease-linear transition-all duration-150"
            type="button"
            onClick={clickedClose}
          >
            Close
          </button>
        ) : null}
        {activeUser && activeUser.user_type === "admin" && (
          <button
            className="bg-emerald-500 text-white active:bg-emerald-600 font-bold uppercase text-sm px-6 py-3 rounded shadow hover:shadow-lg outline-none focus:outline-none mr-1 mb-1 ease-linear transition-all duration-150"
            type="button"
            onClick={save}
            disabled={savingNode}
          >
            {savingNode ? (
              <>
                <i className="fas fa-circle-notch fa-spin"></i> Saving...
              </>
            ) : (
              <>Save Changes</>
            )}
          </button>
        )}
      </div>
    </>
  );
};

export default NodeForm;
